import React, { useMemo } from 'react';
import DisableDotIcon from '@assets/icons/admin-center/table/disable-dot.svg';
import PlusIcon from '@assets/icons/admin-center/table/plus.svg';
import DeleteIcon from '@assets/icons/admin-center/table/trash.svg';
import SuccessDotIcon from '@assets/icons/dots/accepted.svg';
import ArrowDownIcon from '@assets/icons/table/arrow-down.svg';
import ArrowUpIcon from '@assets/icons/table/arrow-up.svg';
import type { SortValue } from '@components/table';
import { ROLES, TENANT_CLIENT_BB_NAME } from '@constants/roles.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { useUserRoles } from '@hooks/useUserRoles.ts';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import {
    setIsActivateTenantModalOpened,
    setIsDeactivateTenantModalOpened,
    setIsDeleteTenantModalOpened,
} from '@routes/sidebar/admin-center/___shared___/model/adminCenter.slice.ts';
import {
    setClientEditContext,
    setClientInfoId,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/model/clientInfo.slice.ts';
import {
    setIsAddNewTenantContext,
    setTenantInfoFullDetails,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import {
    CLIENTS_COLUMN_KEY,
    DEACTIVATE_TENANT_KEY,
    DELETE_TENANT_KEY,
    EDIT_CLIENT_KEY,
    EDIT_TENANT_KEY,
    TENANTS_COLUMN_KEY,
} from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/constants.ts';
import {
    dispatchTenantActionsContext,
    getTenant,
    gotoClientInfo,
    gotoTenantInfo,
    handleClientAndTenantRelatedActions,
} from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/tenantClientActions.ts';
import type { DataType } from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/types.ts';
import { translateItem } from '@utils/translations.ts';
import { Dropdown } from 'antd';
import type { ColumnGroupType, ColumnType } from 'antd/es/table';
import type { ColumnTitle } from 'antd/es/table/interface';

import {
    ActionIconWrapper,
    ActionsTitleWrapper,
    TableStatusWrapper,
    TitleColumnWrapper,
    TitleStatusWrapper,
} from './styled.ts';

// --------------------------ActionMenu ---------------------//
export type ActionScope = 'groupActions' | 'tenant';

const renderAddNewTenant = (icon?: React.ReactNode, text?: string, deleteTenant?: boolean, disabled?: boolean) => (
    <ActionsTitleWrapper $deleteTenant={deleteTenant} $disabled={disabled}>
        {icon}
        {text}
    </ActionsTitleWrapper>
);

const scopeFromRow = (row?: Partial<DataType>): ActionScope => (row?.__isGroupRow ? 'groupActions' : 'tenant');

const buildActionMenuItems = (
    translate: (descriptor: MessageDescriptor) => string,
    canBeActivated: boolean,
    isTenantActive: boolean,
    disableDeleteAdminCenterClientAndTenant: boolean,
    isBlueByteAdmin: boolean,
    rowOrScope?: Partial<DataType> | ActionScope,
) => {
    const scope = typeof rowOrScope === 'string' ? rowOrScope : scopeFromRow(rowOrScope);
    const shouldHideActivateAction = !isTenantActive && !canBeActivated;
    const isDeactivateDisabled = disableDeleteAdminCenterClientAndTenant || (isTenantActive && !isBlueByteAdmin);
    const isDeleteDisabled = disableDeleteAdminCenterClientAndTenant || !isBlueByteAdmin;

    if (scope === 'groupActions') {
        return [
            {
                key: 'add-tenant',
                label: renderAddNewTenant(<PlusIcon />, translate(msg`Add new tenant`)),
            },
        ];
    }

    return [
        { key: EDIT_CLIENT_KEY, label: renderAddNewTenant(undefined, translate(msg`Edit client info`)) },
        { key: EDIT_TENANT_KEY, label: renderAddNewTenant(undefined, translate(msg`Edit tenant`)) },
        ...(shouldHideActivateAction
            ? []
            : [
                  {
                      key: DEACTIVATE_TENANT_KEY,
                      disabled: isDeactivateDisabled,
                      label: renderAddNewTenant(
                          undefined,
                          isTenantActive ? translate(msg`Deactivate tenant`) : translate(msg`Activate tenant`),
                          false,
                          isDeactivateDisabled,
                      ),
                  },
              ]),

        {
            key: DELETE_TENANT_KEY,
            disabled: isDeleteDisabled,
            label: renderAddNewTenant(<DeleteIcon />, translate(msg`Delete tenant`), true, isDeleteDisabled),
        },
    ];
};

// small reusable cell renderer
export const ActionMenu: React.FC<{ row?: DataType; overrideScope?: ActionScope }> = ({ row, overrideScope }) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();
    const navigate = useCustomNavigate();
    const userRoles = useUserRoles();

    const rawClientsData = useAppSelector((s) => s.adminCenterClients.clients.response?.data || []);
    const scope = overrideScope ?? scopeFromRow(row);

    // ← Your suggested lookup (with a tiny fallback for group rows)
    const client = useMemo(() => {
        const label = row?.client ?? '';

        return rawClientsData.find((c: any) => c?.name === label) ?? null;
    }, [row, rawClientsData]);

    const isTenantActive = Boolean(row?.active);
    const canBeActivated = Boolean(row?.canBeActivated);
    const isBlueByteAdmin = userRoles.includes(ROLES.BLUEBYTE_ADMIN);

    // disabled delete action for admin-center client and tenant, because after deletion the system will stop work correct
    const disableDeleteAdminCenterClientAndTenant = row?.tenantName === TENANT_CLIENT_BB_NAME;
    const items = useMemo(
        () =>
            buildActionMenuItems(
                t,
                canBeActivated,
                isTenantActive,
                disableDeleteAdminCenterClientAndTenant,
                isBlueByteAdmin,
                scope,
            ),
        [t, canBeActivated, disableDeleteAdminCenterClientAndTenant, isBlueByteAdmin, isTenantActive, scope],
    );

    const handleMenuClick = ({ key }: { key: string }) => {
        if (!row) return;

        // scope === 'tenant'
        if (!client) return;

        const tenantName = row?.tenantName ?? '';
        const tenant = getTenant(client, tenantName, row);

        if (scope === 'groupActions') {
            if (!client) return;
            // “Add tenant” under this client: set client context and go to Tenant form (create mode)
            dispatch(setClientEditContext({ id: client.id, name: client.name }));
            dispatch(setIsAddNewTenantContext(true));
            dispatch(setClientInfoId(client.id));
            dispatch(setTenantInfoFullDetails(null));
            gotoTenantInfo(navigate, client.id, tenant?.alias);
            return;
        }

        if (key === EDIT_CLIENT_KEY) {
            handleClientAndTenantRelatedActions(client, tenant, Boolean(row.active), dispatch);
            gotoClientInfo(navigate, client.id, tenant?.alias);
            return;
        }

        if (key === EDIT_TENANT_KEY) {
            if (!tenant) return;

            handleClientAndTenantRelatedActions(client, tenant, Boolean(row.active), dispatch);
            gotoTenantInfo(navigate, client.id, tenant.alias);
            return;
        }

        if (key === DEACTIVATE_TENANT_KEY) {
            dispatch(row.active ? setIsDeactivateTenantModalOpened(true) : setIsActivateTenantModalOpened(true));
            dispatchTenantActionsContext(client, tenant, Boolean(row.active), dispatch);
        }

        if (key === DELETE_TENANT_KEY) {
            dispatch(setIsDeleteTenantModalOpened(true));
            dispatchTenantActionsContext(client, tenant, Boolean(row.active), dispatch);
        }
    };

    return (
        <Dropdown
            menu={{ items, onClick: handleMenuClick }}
            prefixCls={'admin-client-list-table-actions-dropdown'}
            trigger={['click']}
        >
            <ActionIconWrapper />
        </Dropdown>
    );
};
// --------------------------ActiveStatusPill ---------------------//
export const renderStatusPill = (isActive: boolean, label: string) => (
    <TableStatusWrapper $isActive={isActive}>
        {isActive ? <SuccessDotIcon /> : <DisableDotIcon />}
        <TitleStatusWrapper $isActive={isActive}>{translateItem(msg`${label}`)}</TitleStatusWrapper>
    </TableStatusWrapper>
);

// --------------------------tableSizing ---------------------//
const TITLE_FONT = '500 14px Poppins';
const CELL_FONT = '400 14px Poppins';
const TITLE_PADDING_X = 32;
const MIN_COL_WIDTH = 100;
const MAX_COL_WIDTH = 360;

const measureTextWidth = (text: string, font = CELL_FONT): number => {
    if (typeof document === 'undefined') return Math.ceil(text.length * 8);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 140;
    ctx.font = font;
    return Math.ceil(ctx.measureText(text).width);
};

export const widthFromTitle = <T,>(title: ColumnTitle<T>): number => {
    const text = typeof title === 'string' || typeof title === 'number' ? String(title) : ' ';

    const w = measureTextWidth(text, TITLE_FONT);
    return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, w + TITLE_PADDING_X));
};

export const calculateFixedColWidth = (
    values: (string | number | undefined | null)[],
    headerText: string,
    opts?: { font?: string; padding?: number; min?: number; max?: number },
) => {
    const { font = CELL_FONT, padding = TITLE_PADDING_X, min = 140, max = 520 } = opts || {};
    const longest = Math.max(
        measureTextWidth(headerText, TITLE_FONT),
        ...values.map((v) => measureTextWidth(String(v ?? ''), font)),
    );
    return Math.min(max, Math.max(min, longest + padding));
};

export const isLeafColumn = <T,>(col: ColumnType<T> | ColumnGroupType<T>): col is ColumnType<T> => !('children' in col);

type WidthMap = Partial<Record<'clients' | 'tenants', number>>;

export const sizeBaseColumns = <T,>(
    cols: Array<ColumnType<T> | ColumnGroupType<T>>,
    widths: WidthMap,
    calcTitleWidth: (title: ColumnTitle<T>) => number,
) => {
    return cols
        .filter((c: any) => !c?.hidden)
        .map((col) => {
            if (!isLeafColumn<T>(col)) return col;

            // left-fixed, dynamic widths
            if (col.key === CLIENTS_COLUMN_KEY || col.dataIndex === CLIENTS_COLUMN_KEY) {
                return { ...col, width: widths.clients, fixed: 'left' as const };
            }
            if (col.key === TENANTS_COLUMN_KEY || col.dataIndex === TENANTS_COLUMN_KEY) {
                return { ...col, width: widths.tenants, fixed: 'left' as const };
            }

            // right-fixed: keep as-is (e.g., operation)
            if (col.key === 'operation' || col.fixed === 'right') {
                return col;
            }

            // others: width from header text
            return { ...col, width: calcTitleWidth(col.title) };
        });
};

// --------------------------groupBy ---------------------//
type TableCol<T> = ColumnType<T> | ColumnGroupType<T>;

const mergeBaseClassNames = (base: any, extra: string) => {
    const prev = base?.className ? String(base.className) : '';
    return prev ? `${prev} ${extra}` : extra;
};

export const applyGroupRowSpans = <T extends { __isGroupRow?: boolean; __groupLabel?: React.ReactNode }>(
    cols: TableCol<T>[],
): TableCol<T>[] => {
    const leftIdxs = cols.map((c, i) => ((c as any).fixed === 'left' ? i : -1)).filter((i) => i !== -1);
    const midIdxs = cols.map((c, i) => ((c as any).fixed ? -1 : i)).filter((i) => i !== -1);

    const firstLeft = leftIdxs[0] ?? -1;
    const firstMid = midIdxs[0] ?? -1;
    const leftCount = leftIdxs.length;
    const midCount = midIdxs.length;

    return cols.map((col, idx) => {
        const prevOnCell = (col as any).onCell;
        const origRender = (col as any).render;
        const isLeftFixed = (col as any).fixed === 'left';
        const isRightFixed = (col as any).fixed === 'right';
        const isMiddle = !(col as any).fixed;

        return {
            ...col,

            onCell: (row: T, ...rest: any[]) => {
                const base = typeof prevOnCell === 'function' ? (prevOnCell(row, ...rest) ?? {}) : {};
                if (!row?.__isGroupRow) return base;

                if (isLeftFixed) {
                    if (idx === firstLeft)
                        return {
                            ...base,
                            colSpan: Math.max(1, leftCount),
                            className: mergeBaseClassNames(base, 'is-group-row'),
                        };
                    return { ...base, colSpan: 0, className: mergeBaseClassNames(base, 'is-group-row') };
                }
                if (isMiddle) {
                    if (idx === firstMid)
                        return {
                            ...base,
                            colSpan: Math.max(1, midCount),
                            className: mergeBaseClassNames(base, 'is-group-row'),
                        };
                    return { ...base, colSpan: 0, className: mergeBaseClassNames(base, 'is-group-row') };
                }
                if (isRightFixed) {
                    return { ...base, className: mergeBaseClassNames(base, 'is-group-row is-group-action') };
                }
                return base;
            },

            render: (value: any, row: T, ...rest: any[]) => {
                if (row?.__isGroupRow) {
                    if (isLeftFixed && idx === firstLeft)
                        return <span className="group-title">{row.__groupLabel ?? value}</span>;
                    if (isMiddle && idx === firstMid) return null;
                    if (isRightFixed) return typeof origRender === 'function' ? origRender(value, row, ...rest) : value;
                    return null;
                }
                return typeof origRender === 'function' ? origRender(value, row, ...rest) : value;
            },
        } as any;
    });
};

export const toActiveBool = (v: DataType['active']): boolean => v === true;

export const getRowClassName = <T extends { __isGroupRow?: boolean; __allInactive?: boolean; active?: boolean }>(
    row: T,
    _index?: number,
): string => {
    if (row.__isGroupRow) {
        return row.__allInactive ? 'is-group-row group-all-inactive' : 'is-group-row';
    }
    return toActiveBool(row.active) ? 'row-active' : 'row-inactive';
};

export const getColumnTitleWithSortIcon = (
    columnTitle: string,
    columnKey: string,
    sort: SortValue | null | undefined,
) => (
    <TitleColumnWrapper>
        <span>{columnTitle}</span>
        {sort?.columnId !== columnKey && null}
        {sort?.columnId === columnKey && sort.order === 'asc' && <ArrowUpIcon />}
        {sort?.columnId === columnKey && sort.order === 'desc' && <ArrowDownIcon />}
    </TitleColumnWrapper>
);
